import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AuthUser, MfaSetup, dismissMfaPrompt, enableMfa, setupMfa } from '../api/auth';
import { appTheme, pitchTheme } from '../styles/theme';
import { Button } from './ui/Button';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const StepText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.55;
  color: ${appTheme.textMuted};

  strong {
    color: ${appTheme.text};
  }
`;

const QrPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px;
  border-radius: ${appTheme.radiusMd};
  border: 1px solid ${appTheme.border};
  background: ${appTheme.pageBg};
`;

const QrImage = styled.img`
  width: 180px;
  height: 180px;
  border-radius: ${appTheme.radiusSm};
  background: ${appTheme.surface};
`;

const SecretCode = styled.code`
  font-size: 0.75rem;
  word-break: break-all;
  text-align: center;
  color: ${appTheme.textMuted};
  background: ${appTheme.surface};
  border: 1px solid ${appTheme.border};
  border-radius: ${appTheme.radiusSm};
  padding: 6px 10px;
`;

const CodeField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.text};
`;

const CodeInput = styled.input`
  padding: 10px 14px;
  border-radius: ${appTheme.radiusMd};
  border: 1px solid ${appTheme.border};
  font-family: inherit;
  font-size: 1rem;
  letter-spacing: 0.35em;
  color: ${appTheme.text};
  background: ${appTheme.pageBg};
  transition: border-color 0.15s ease, outline 0.15s ease;

  &:focus {
    outline: 2px solid ${appTheme.green};
    outline-offset: 1px;
    border-color: ${appTheme.green};
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

const ErrorMessage = styled.p`
  margin: 0;
  padding: 10px 12px;
  border-radius: ${appTheme.radiusMd};
  background: ${appTheme.dangerBg};
  border: 1px solid ${appTheme.dangerBorder};
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.dangerText};
`;

const LoadingMessage = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${appTheme.textMuted};
`;

interface MfaEnrollmentProps {
  onEnabled: (user: AuthUser) => void;
  onSkip?: (user: AuthUser) => void;
}

const MfaEnrollment: React.FC<MfaEnrollmentProps> = ({ onEnabled, onSkip }) => {
  const { t } = useTranslation();
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setupMfa()
      .then((result) => {
        if (!cancelled) {
          setSetup(result);
        }
      })
      .catch((err: Error) => {
        if (!cancelled) {
          setError(err.message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnable = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isBusy) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      onEnabled(await enableMfa(code.trim()));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('mfa.enableError'));
      setIsBusy(false);
    }
  };

  const handleSkip = async () => {
    if (isBusy || !onSkip) {
      return;
    }
    setIsBusy(true);
    setError(null);
    try {
      onSkip(await dismissMfaPrompt());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.somethingWrong'));
      setIsBusy(false);
    }
  };

  return (
    <Wrapper>
      <StepText>{t('mfa.step1')}</StepText>

      {setup ? (
        <QrPanel>
          <QrImage src={setup.qrDataUrl} alt={t('mfa.qrAlt')} />
          <SecretCode>{setup.secret}</SecretCode>
        </QrPanel>
      ) : (
        !error && <LoadingMessage>{t('mfa.preparingQr')}</LoadingMessage>
      )}

      <form onSubmit={handleEnable}>
        <Wrapper>
          <StepText>{t('mfa.step2')}</StepText>
          <CodeField>
            {t('mfa.authCode')}
            <CodeInput
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              minLength={6}
              maxLength={6}
            />
          </CodeField>

          {error && <ErrorMessage>{error}</ErrorMessage>}

          <Actions>
            <Button type="submit" $variant="primary" disabled={isBusy || !setup || code.length !== 6}>
              {isBusy ? t('mfa.verifying') : t('mfa.turnOn')}
            </Button>
            {onSkip && (
              <Button type="button" $variant="ghost" disabled={isBusy} onClick={handleSkip}>
                {t('mfa.skipForNow')}
              </Button>
            )}
          </Actions>
        </Wrapper>
      </form>
    </Wrapper>
  );
};

export default MfaEnrollment;
